/**
 * The man a mission is about (10 §3): the general to be killed, the courier whose satchel is
 * wanted. He is the spawn flagged `objectiveTarget`, looked up once on the first ground tick -
 * or, if he is dormant, when his timed event puts him on the map. Under `targetFlees` he runs
 * for the north edge exactly as mission 4's officer does, and is gone when he gets there.
 */

import { type Ctx, isAlive, isFighter } from '../ctx';
import { EXPANSION, VEHICLES } from '../tuning';
import type { Enemy, World } from '../types';
import { completeObjective, dist, failObjective, targetName } from './common';

/** `stepFlee` gives up when its timer runs out; a fleeing man's is kept topped up to this. */
const FLEE_KEEPALIVE_TICKS = 600;

/** The target, wherever he is and whatever state he is in; null if he is not on the map. */
export function targetEnemy(ctx: Pick<Ctx, 'world'>): Enemy | null {
  const id = ctx.world.objective.targetEnemyId;
  if (id === -1) return null;
  return ctx.world.enemies.find((e) => e.id === id) ?? null;
}

/**
 * Where the man of the mission is, this tick, for a LIVE mark on the strip map (ROUND5 §2.7).
 *
 * The marker audit found missions 26 and 29 marking a proxy that stands still while the man walks away from
 * it: the general does his round of the compound and is then hurried into his car, the courier never sets
 * foot on the road at all until something stops the one he is riding in. Nothing static can mark either, so
 * this says where he is now. `riding`: he is inside that vehicle and the mark is the vehicle's. `down`: he is
 * out of the fight - on a `lootTarget` that is the body still to be searched.
 *
 * Null when there is nobody to mark: not that kind of mission, he is not on the map yet (`targetStillDormant`
 * says so), he has got away, or the job at him is done. Pure: it reads the world and decides nothing.
 */
export function targetMark(world: World): { x: number; z: number; riding: boolean; down: boolean } | null {
  const o = world.objective;
  // A kill is over the moment he is down; a satchel is not, and until it is taken his body is still the
  // place to walk to - `complete` is what tells the two apart, and it is set by each kind's own step.
  if (o.complete || o.failed || o.targetGone) return null;
  if (o.targetAboardId !== -1) {
    const car = world.enemies.find((v) => v.id === o.targetAboardId);
    return car === undefined ? null : { x: car.x, z: car.z, riding: true, down: false };
  }
  const man = o.targetEnemyId === -1 ? undefined : world.enemies.find((e) => e.id === o.targetEnemyId);
  return man === undefined ? null : { x: man.x, z: man.z, riding: false, down: !isFighter(man) };
}

/** Is the target still to come: a dormant spawn nobody has woken yet? */
export function targetStillDormant(ctx: Pick<Ctx, 'world' | 'statics'>): boolean {
  const o = ctx.world.objective;
  if (o.targetEnemyId !== -1 || o.targetGone) return false;
  // R4: a man who is riding is still to come - the vehicle carrying him has not been stopped yet.
  return ctx.statics.def.spawns
    .some((s) => (s.objectiveTarget === true || s.carriesTarget === true) && s.dormantUntil !== undefined);
}

/**
 * R4: the car sent for him (mission 29). A fleeing man makes for the nearest live vehicle instead of the
 * north edge, gets in, and is gone when it drives off - or goes up with it if the ranger stops it first.
 */
function getawayCar(ctx: Ctx, e: Enemy): Enemy | null {
  let best: Enemy | null = null;
  let bestD: number = VEHICLES.waitRadius;
  for (const v of ctx.world.enemies) {
    if (v.vehicle === undefined || v.vehicle.gone || !isAlive(v)) continue;
    const d = dist(v.x, v.z, e.x, e.z);
    if (d > bestD) continue;
    bestD = d;
    best = v;
  }
  return best;
}

function board(ctx: Ctx, e: Enemy, car: Enemy): void {
  const { world: w } = ctx;
  const o = w.objective;
  if (car.vehicle) car.vehicle.aboard++;
  o.targetAboardId = car.id;
  o.targetEnemyId = -1;
  w.enemies.splice(w.enemies.indexOf(e), 1);
  if (w.runnerId === e.id) w.runnerId = -1;
}

/** He is in the car: it carries him off the map (lost), or the ranger stops it and he is in the wreck (done). */
function rideAway(ctx: Ctx): void {
  const o = ctx.world.objective;
  const car = ctx.world.enemies.find((v) => v.id === o.targetAboardId);
  if (!car) {
    o.targetAboardId = -1;
    targetEscaped(ctx);
    return;
  }
  if (car.state !== 'dead') return;
  // A vehicle that carries him as a passenger puts him out of its wreck as a body (specials/vehicleCrew.ts):
  // there is a man to loot, so the mission is not settled here.
  if (car.vehicle?.targetAboard === true) return;
  o.targetAboardId = -1;
  o.done = 1;
  completeObjective(ctx);
}

/** Why the objective is lost when he gets away: the `targetFlees` rule's own words, if it has any. */
function escapeReason(ctx: Ctx): string {
  for (const rule of ctx.statics.def.objective.rules) {
    if (rule.type === 'alertConsequence' && rule.effect === 'targetFlees' && rule.reason !== undefined) return rule.reason;
  }
  return `${targetName(ctx.statics.def, 'THE TARGET')} GOT AWAY`;
}

/** He got clear - off the north edge, or by never coming at all. */
export function targetEscaped(ctx: Ctx): void {
  const o = ctx.world.objective;
  o.targetGone = true;
  o.targetFleeing = false;
  failObjective(ctx, escapeReason(ctx));
}

function resolveTarget(ctx: Ctx): void {
  const o = ctx.world.objective;
  if (o.targetEnemyId !== -1 || o.groundTicks > 0) return;
  const spawn = ctx.statics.def.spawns.find((s) => s.objectiveTarget === true && s.dormantUntil === undefined);
  if (!spawn) return;
  const e = ctx.world.enemies.find((en) => en.kind === spawn.kind && en.homeX === spawn.x && en.homeZ === spawn.z);
  if (e) o.targetEnemyId = e.id;
}

/** Every ground tick, failed objective or not: the world goes on, and a running man keeps running. */
export function stepTarget(ctx: Ctx): void {
  const { world: w, statics } = ctx;
  const o = w.objective;
  resolveTarget(ctx);
  if (o.targetAboardId !== -1) {
    rideAway(ctx);
    return;
  }
  const e = targetEnemy(ctx);
  if (!e || !o.targetFleeing || !isFighter(e) || e.state === 'down') return;
  const car = getawayCar(ctx, e);
  if (car) {
    // Hurried to his car: he runs to it, not to the edge, and the car does not pull away without him.
    if (dist(e.x, e.z, car.x, car.z) <= VEHICLES.dismountReach) {
      board(ctx, e, car);
      return;
    }
    if (e.state !== 'flee' || !e.hasSpot || dist(e.spotX, e.spotZ, car.x, car.z) > 1) {
      e.state = 'flee';
      e.hasSpot = true;
      e.spotX = car.x;
      e.spotZ = car.z;
      e.path = [];
    }
    e.stateTicks = Math.max(e.stateTicks, FLEE_KEEPALIVE_TICKS);
    return;
  }
  if (e.z <= EXPANSION.fleeEdgeMargin) {
    w.enemies.splice(w.enemies.indexOf(e), 1);
    if (w.runnerId === e.id) w.runnerId = -1;
    targetEscaped(ctx);
    return;
  }
  // Borrow the soldiers' own flight: due north, and he does not stop to fight.
  if (e.state !== 'flee') {
    e.state = 'flee';
    e.hasSpot = true;
    e.spotX = Math.min(statics.def.sizeX - 2, Math.max(2, e.x));
    e.spotZ = 1.5;
    e.path = [];
  }
  e.stateTicks = Math.max(e.stateTicks, FLEE_KEEPALIVE_TICKS);
}

export function stepKillTarget(ctx: Ctx): void {
  const e = targetEnemy(ctx);
  // Out of the fight is enough: nobody is asked to finish a wounded man (02 §11).
  if (!e || isFighter(e)) return;
  ctx.world.objective.done = 1;
  completeObjective(ctx);
}

export function stepLootTarget(ctx: Ctx): void {
  if (!ctx.world.objective.targetLooted) return;
  ctx.world.objective.done = 1;
  completeObjective(ctx);
}

/** The target's body, if it is down and within reach and still has what he came for. */
export function lootableBody(ctx: Pick<Ctx, 'world' | 'statics'>): Enemy | null {
  const { world: w, statics } = ctx;
  if (statics.def.objective.kind !== 'lootTarget' || w.objective.targetLooted) return null;
  const e = targetEnemy(ctx);
  if (!e || isFighter(e)) return null;
  return dist(e.x, e.z, w.ranger.x, w.ranger.z) <= EXPANSION.lootReach ? e : null;
}
